/* eslint-disable @typescript-eslint/no-unsafe-return */

/* eslint-disable @typescript-eslint/no-explicit-any */
import type { HttpMethod } from 'api-v4-types'
import type { ImageResponse } from 'next/og'
import type { NextResponse } from 'next/server'
import type { GET as getActivityFeed } from './activity-feed/[slug]/[cursor]/route'
import type { GET as getCurrentUserLoggedIn } from './current-user/logged-in/route'
import type { GET as getCurrentUser } from './current-user/route'
import type { GET as getFonts } from './fonts/route'
import type { GET as getAdminBoxData } from './fundraising-event/admin-box/[slug]/route'
import type { GET as getDonationFeedHistory } from './fundraising-event/donation-feed/[slug]/history/[cursor]/route'
import type { GET as getDonationFeed } from './fundraising-event/donation-feed/[slug]/route'
import type { GET as getShareImage } from './fundraising-event/image/[locale]/[slug]/route'
import type { POST as postFlushCache } from './internal/flush-cache/route'
import type { POST as postRevalidateTags } from './internal/revalidate-tags/route'
import type { GET as getLivez } from './livez/route'
import type { GET as getReadyz } from './readyz/route'
import type { GET as getMatchingEvents } from './matching-events/[locale]/projects/[slug]/route'
import type { GET as getOrganisations } from './organisations/[locale]/[query]/route'
import type { GET as getWidgetSettings } from './widget-settings/[slug]/route'
import type { GET as getWidgetSettingsWithQuestion } from './widget-settings/with-question/[slug]/route'
import { Routes } from '@/helpers/utils'
import type { AppRouteHandlerRoutes } from '@/helpers/utils/routing/Routes/routeManifest'
import { ApiRouteNames } from '@/helpers/utils/routing/Routes/RouteNames'
import type { RouteDefinitions } from '@/helpers/utils/routing/Routes/Routes'
import type { UnpackPromise } from '@/types'
import type { GET as getFundraisingEventsManageLiveWidgetsVotingData } from 'src/app/[locale]/(standard)/(manage)/fundraising-events/[slug]/manage/live-widgets/@widgets/voting/data/route.ts'
type UnpackNextResponse<T> =
  T extends NextResponse<infer R> ? Exclude<R, { error: any }> : UnpackNextResponse<UnpackPromise<T>>

type NextResponseRouteHandler =
  | ((...args: any[]) => NextResponse<any>)
  | ((...args: any[]) => Promise<NextResponse<any>>)
type ImageResponseRouteHandler = ((...args: any[]) => ImageResponse) | ((...args: any[]) => Promise<ImageResponse>)
type RouteHandler = NextResponseRouteHandler | ImageResponseRouteHandler
type TransformType = 'json' | 'blob' | 'text'
type WrappedFetchReturn<
  THandler extends RouteHandler,
  TTranformType extends TransformType,
> = TTranformType extends 'json'
  ? THandler extends NextResponseRouteHandler
    ? UnpackNextResponse<ReturnType<THandler>>
    : never
  : TTranformType extends 'blob'
    ? Blob
    : TTranformType extends 'text'
      ? string
      : never

type TypeAtPath<T extends Record<string, unknown>, P extends string> = P extends `${infer P1}.${infer P2}`
  ? P1 extends keyof T
    ? T[P1] extends Record<string, unknown>
      ? P2 extends keyof T[P1]
        ? T[P1][P2]
        : never
      : never
    : never
  : P extends keyof T
    ? T[P]
    : never

type PathsOf<T extends Record<string, unknown>, Prev extends string = ''> = {
  [K in keyof T & (string | number)]: T[K] extends Record<string, unknown>
    ? T[K] extends Array<any>
      ? Prev extends ''
        ? `${K}`
        : `${Prev}.${K}`
      : (Prev extends '' ? `${K}` : `${Prev}.${K}`) | PathsOf<T[K], Prev extends '' ? `${K}` : `${Prev}.${K}`>
    : Prev extends ''
      ? `${K}`
      : `${Prev}.${K}`
}[keyof T & (string | number)]

async function transformResponse(res: Response, transformType: TransformType): Promise<any> {
  switch (transformType) {
    case 'blob':
      return await res.blob()
    case 'text':
      return await res.text()
    default:
      return await res.json()
  }
}

function getApiCall<TTranformType extends TransformType, TRouteBuilder extends (...args: any[]) => string>(
  routeFn: TRouteBuilder,
  transform: TTranformType,
  method?: HttpMethod
) {
  return async <THandler extends RouteHandler>(
    ...args: [...Parameters<TRouteBuilder>, init?: RequestInit]
  ): Promise<WrappedFetchReturn<THandler, TTranformType>> => {
    const init = args[2] ?? {}
    const url = routeFn(...(args as unknown as Parameters<TRouteBuilder>))
    const res = await fetch(url, { method, ...init })
    let error: { error: any; cause?: any } | null = null
    if (res.ok) return await transformResponse(res, transform)
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      error = await res.json()
    } catch {
      error = { error: 'Call to API failed', cause: { status: res.status } }
    }
    console.error({ error, status: res.status, request: { method, init, url } })
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw error
  }
}
type ApiRouteDefinitions = RouteDefinitions<AppRouteHandlerRoutes>
type ApiRouteDefinitionMap = Record<keyof ApiRouteDefinitions, (...args: any[]) => any>

export { ApiRouteNames }
const TypedApi = {
  [ApiRouteNames.apiActivityFeed]: getApiCall(Routes.apiActivityFeed, 'json')<typeof getActivityFeed>,
  [ApiRouteNames.apiCurrentUser]: getApiCall(Routes.apiCurrentUser, 'json')<typeof getCurrentUser>,
  [ApiRouteNames.apiCurrentUserLoggedIn]: getApiCall(Routes.apiCurrentUserLoggedIn, 'json')<
    typeof getCurrentUserLoggedIn
  >,
  [ApiRouteNames.apiFonts]: getApiCall(Routes.apiFonts, 'json')<typeof getFonts>,
  [ApiRouteNames.apiLivez]: getApiCall(Routes.apiLivez, 'json')<typeof getLivez>,
  [ApiRouteNames.apiReadyz]: getApiCall(Routes.apiReadyz, 'json')<typeof getReadyz>,
  [ApiRouteNames.apiOrganisations]: getApiCall(Routes.apiOrganisations, 'json')<typeof getOrganisations>,
  [ApiRouteNames.apiWidgetSettings]: getApiCall(Routes.apiWidgetSettings, 'json')<typeof getWidgetSettings>,
  [ApiRouteNames.apiWidgetSettingsWithQuestion]: getApiCall(Routes.apiWidgetSettingsWithQuestion, 'json')<
    typeof getWidgetSettingsWithQuestion
  >,
  [ApiRouteNames.apiInternalFlushCache]: getApiCall(Routes.apiInternalFlushCache, 'json', 'post')<
    typeof postFlushCache
  >,
  [ApiRouteNames.apiFundraisingEventDonationFeed]: getApiCall(Routes.apiFundraisingEventDonationFeed, 'json')<
    typeof getDonationFeed
  >,
  [ApiRouteNames.apiFundraisingEventAdminBox]: getApiCall(Routes.apiFundraisingEventAdminBox, 'json')<
    typeof getAdminBoxData
  >,
  [ApiRouteNames.apiFundraisingEventImage]: getApiCall(Routes.apiFundraisingEventImage, 'blob')<typeof getShareImage>,
  [ApiRouteNames.apiMatchingEventsProjects]: getApiCall(Routes.apiMatchingEventsProjects, 'json')<
    typeof getMatchingEvents
  >,
  [ApiRouteNames.apiInternalRevalidateTags]: getApiCall(Routes.apiInternalRevalidateTags, 'json', 'post')<
    typeof postRevalidateTags
  >,
  [ApiRouteNames.apiFundraisingEventDonationFeedHistory]: getApiCall(
    Routes.apiFundraisingEventDonationFeedHistory,
    'json'
  )<typeof getDonationFeedHistory>,
  [ApiRouteNames.fundraisingEventsManageLiveWidgetsVotingData]: getApiCall(
    Routes.fundraisingEventsManageLiveWidgetsVotingData,
    'json'
  )<typeof getFundraisingEventsManageLiveWidgetsVotingData>,
} as const satisfies ApiRouteDefinitionMap
export type ApiPaths = PathsOf<typeof TypedApi>

export type ApiParams<TPath extends ApiPaths> = Parameters<
  Extract<TypeAtPath<typeof TypedApi, TPath>, (...args: any[]) => any>
>[0]
export type ApiFetcher<TPath extends ApiPaths> = Extract<TypeAtPath<typeof TypedApi, TPath>, (...args: any[]) => any>
export type ApiReturnType<TPath extends ApiPaths> = UnpackPromise<
  ReturnType<Extract<TypeAtPath<typeof TypedApi, TPath>, (...args: any[]) => any>>
>

export default TypedApi
